import { ShieldCheck } from 'lucide-react';
import { useCallback, useState, type FormEvent, type ReactNode } from 'react';

import {
  AdminSectionCard,
  ConfirmDialog,
  EmptyState,
  HelpAnchor,
  InlineError,
  InputField,
  Pill,
  SkeletonBlock,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import {
  BUTTON_DANGER,
  BUTTON_GHOST,
  BUTTON_PRIMARY,
  SECTION_DIVIDER,
} from '@/shared/ui-class';
import { ADMIN_SEED_ANCHOR_MS, SEED_SSO_STATUS } from '@/versions/v1/shared/admin-seed';
import { AdminSceneShell } from './admin-shared';
import {
  EMPTY_SSO_FORM,
  fieldForValidationMessage,
  formatSsoRelativeTime,
  formatSsoTimestamp,
  mockClearSso,
  mockSaveSso,
  rotationFreshness,
  sourceLabel,
  sourceTone,
  type SsoFormState,
  type SsoOidcStatus,
} from './SsoScene.transform';

function StatusRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-divider py-1.5 text-sm last:border-0">
      <span className="text-muted">{label}</span>
      <span className="text-right text-zinc-900 dark:text-zinc-100">{value}</span>
    </div>
  );
}

export function SsoScene() {
  const [status, setStatus] = useState<SsoOidcStatus>(SEED_SSO_STATUS);
  const [loading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [form, setForm] = useState<SsoFormState>(EMPTY_SSO_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof SsoFormState, string>>>({});
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);

  const [clearing, setClearing] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  const [lockoutConfirm, setLockoutConfirm] = useState<{ affectedUserCount: number } | null>(
    null,
  );

  const loadStatus = useCallback(() => {
    setLoadError(null);
    setStatus(SEED_SSO_STATUS);
  }, []);

  void loadStatus;

  const updateField = (field: keyof SsoFormState, value: string): void => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setFormError(null);
    setFieldErrors({});
    setSaveSuccess(false);
    setStatus((prev) => mockSaveSso(prev, form, ADMIN_SEED_ANCHOR_MS));
    setForm((prev) => ({ ...prev, clientSecret: '' }));
    setSaveSuccess(true);
    setRotateOpen(false);
    setSubmitting(false);
  };

  const performClear = useCallback(
    (confirm?: boolean): boolean => {
      setClearing(true);
      setClearError(null);
      if (!confirm && status.source === 'db') {
        setLockoutConfirm({ affectedUserCount: 2 });
        setClearing(false);
        return false;
      }
      setStatus(mockClearSso(status));
      setLockoutConfirm(null);
      setClearing(false);
      return true;
    },
    [status],
  );

  const handleClearClick = (): void => {
    performClear();
  };

  const handleConfirmClear = async (): Promise<void> => {
    if (!lockoutConfirm) return;
    const ok = performClear(true);
    if (!ok) throw new Error('clear failed');
  };

  const panel = (() => {
    if (loading) {
      return (
        <AdminSectionCard title="Microsoft Entra ID">
          <div className="space-y-2">
            <SkeletonBlock className="h-5 w-[60%]" />
            <SkeletonBlock className="h-5 w-[40%]" />
            <SkeletonBlock className="h-5 w-[50%]" />
          </div>
        </AdminSectionCard>
      );
    }

    if (loadError || !status) {
      return (
        <EmptyState
          icon={<ShieldCheck className="h-8 w-8 text-zinc-400" />}
          title="Couldn't load SSO status"
          description={loadError ?? 'Unknown error'}
        />
      );
    }

    const isRotate = status.source === 'db';
    const now = ADMIN_SEED_ANCHOR_MS;
    const freshness = rotationFreshness(status.rotatedAt, now);
    const submitDisabled =
      submitting || !form.tenantId.trim() || !form.clientId.trim() || !form.clientSecret.trim();

    return (
      <div className="space-y-4">
        <AdminSectionCard
          title="Microsoft Entra ID"
          description="Sign-in is enabled by valid credentials, not a toggle here."
          actions={<Pill tone={sourceTone(status.source)}>{sourceLabel(status.source)}</Pill>}
        >
          <StatusRow label="Tenant ID" value={status.tenantId ?? '—'} />
          <StatusRow
            label="Client secret"
            value={status.last4 ? `••••${status.last4}` : '—'}
          />
          <StatusRow
            label="Last rotated"
            value={
              status.rotatedAt === null ? (
                'Never'
              ) : (
                <span
                  className="inline-flex items-center gap-2"
                  title={formatSsoTimestamp(status.rotatedAt)}
                >
                  {formatSsoRelativeTime(status.rotatedAt, now)}
                  {freshness ? <Pill tone={freshness.tone}>{freshness.label}</Pill> : null}
                </span>
              )
            }
          />
          <StatusRow label="Set by" value={status.setByEmail ?? '—'} />
        </AdminSectionCard>

        {status.source === 'env' ? (
          <p className="text-xs text-muted">
            These credentials come from environment variables. Saving the form below creates a
            database row that overrides the env vars — the DB row wins from that point on.
          </p>
        ) : null}
        {status.source === 'none' ? (
          <p className="text-xs text-muted">
            No SSO credentials configured. Set them via environment variables or the form below.
          </p>
        ) : null}

        <AdminSectionCard
          title={isRotate ? 'Rotate credentials' : 'Set credentials'}
          description="Credentials are verified against Microsoft before they're saved."
        >
          {isRotate && !rotateOpen ? (
            <div className={SECTION_DIVIDER}>
              <p className="pt-3 text-sm text-muted">
                The client secret is stored encrypted and shown only once — it is never displayed
                again. Rotating re-enters all three fields and re-verifies against Microsoft before
                saving.
              </p>
              <div className="mt-3">
                <button
                  type="button"
                  className={BUTTON_GHOST}
                  onClick={() => setRotateOpen(true)}
                >
                  Rotate secret
                </button>
              </div>
            </div>
          ) : (
            <form className="space-y-3" onSubmit={handleSubmit}>
              <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Tenant ID
                <InputField
                  className="mt-1"
                  autoComplete="off"
                  spellCheck={false}
                  value={form.tenantId}
                  onChange={(e) => updateField('tenantId', e.target.value)}
                  disabled={submitting}
                />
              </label>
              {fieldErrors.tenantId ? <InlineError>{fieldErrors.tenantId}</InlineError> : null}

              <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Client ID
                <InputField
                  className="mt-1"
                  autoComplete="off"
                  spellCheck={false}
                  value={form.clientId}
                  onChange={(e) => updateField('clientId', e.target.value)}
                  disabled={submitting}
                />
              </label>
              {fieldErrors.clientId ? <InlineError>{fieldErrors.clientId}</InlineError> : null}

              <label className="block text-sm font-medium text-zinc-900 dark:text-zinc-100">
                Client secret
                <InputField
                  className="mt-1"
                  type="password"
                  autoComplete="off"
                  spellCheck={false}
                  value={form.clientSecret}
                  onChange={(e) => updateField('clientSecret', e.target.value)}
                  disabled={submitting}
                />
              </label>
              {fieldErrors.clientSecret ? (
                <InlineError>{fieldErrors.clientSecret}</InlineError>
              ) : null}
              <p className="text-xs text-muted">
                Stored encrypted; shown once — never displayed again.
              </p>
              {isRotate ? (
                <p className="text-xs text-muted">
                  Rotating requires re-entering all three fields — this isn&apos;t a partial
                  update.
                </p>
              ) : null}

              {formError ? <InlineError>{formError}</InlineError> : null}
              {saveSuccess && !formError ? (
                <p className="text-sm text-green-700 dark:text-green-400">
                  Saved — connection verified.
                </p>
              ) : null}

              <div className="flex items-center gap-2">
                <button type="submit" className={BUTTON_PRIMARY} disabled={submitDisabled}>
                  {submitting ? 'Verifying & saving…' : 'Verify & save'}
                </button>
                {isRotate ? (
                  <button
                    type="button"
                    className={BUTTON_GHOST}
                    disabled={submitting}
                    onClick={() => {
                      setRotateOpen(false);
                      setForm((prev) => ({ ...prev, clientSecret: '' }));
                      setFormError(null);
                      setFieldErrors({});
                    }}
                  >
                    Cancel
                  </button>
                ) : null}
              </div>
            </form>
          )}
        </AdminSectionCard>

        {status.source === 'db' ? (
          <AdminSectionCard title="Clear configuration">
            <div className={SECTION_DIVIDER}>
              <p className="pt-3 text-sm text-muted">
                Removes the stored credentials. If no environment-variable fallback is configured,
                users who sign in only via SSO will be locked out.
              </p>
              {clearError ? (
                <div className="mt-2">
                  <InlineError>{clearError}</InlineError>
                </div>
              ) : null}
              <button
                type="button"
                className={`mt-3 ${BUTTON_DANGER}`}
                disabled={clearing}
                onClick={handleClearClick}
              >
                {clearing ? 'Clearing…' : 'Clear'}
              </button>
            </div>
          </AdminSectionCard>
        ) : null}

        <ConfirmDialog
          open={lockoutConfirm !== null}
          onOpenChange={(open) => {
            if (!open) setLockoutConfirm(null);
          }}
          error={lockoutConfirm ? clearError : undefined}
          title="Clear SSO configuration?"
          description={
            lockoutConfirm ? (
              <>
                Clearing SSO config will lock out {lockoutConfirm.affectedUserCount} user(s) who
                sign in only via SSO. This action cannot be undone.
              </>
            ) : null
          }
          confirmLabel="Clear and lock out"
          tone="danger"
          onConfirm={async () => {
            await handleConfirmClear();
          }}
        />
      </div>
    );
  })();

  return (
    <AdminSceneShell
      sceneName="SsoScene"
      title="Single sign-on"
      description="Configure Microsoft Entra ID SSO."
      headerActions={<HelpAnchor doc="workspace/admin" />}
      stackClassName="space-y-3"
    >
      <Commentable name="Panel">
        {panel}
      </Commentable>
    </AdminSceneShell>
  );
}

// Re-export for parity extractor visibility when validating validation paths.
void fieldForValidationMessage;
